import { Permission, TenantConfigPlane } from '@today/types'

/** What the console's Organization section may show and do (TO-389 organization-admin-ui REQ-001). */
export interface OrganizationCapabilities {
	/** Platform plane: render the notice only. */
	platformNotice: boolean
	/** Tenant plane with no organization read permission. */
	forbidden: boolean
	/** False whenever no `/organization/*` request may be sent. */
	fetchAllowed: boolean
	tabs: { units: boolean; types: boolean }
	panels: { assignments: boolean }
	write: { units: boolean; types: boolean; assignments: boolean }
	/** `user.read`: display names and the user picker. */
	userDirectory: boolean
}

export interface OrganizationCapabilityInput {
	plane: TenantConfigPlane
	/** The manifest granted READ only. */
	readOnly: boolean
	permissions: readonly string[]
}

const NONE = {
	tabs: { units: false, types: false },
	panels: { assignments: false },
	write: { units: false, types: false, assignments: false },
	userDirectory: false,
}

/**
 * The pure capability map. The organization API resolves the tenant from the
 * caller's principal and has no platform-plane routes, so the platform plane
 * never fetches (it would show the caller's own tenant). A write control needs
 * both manifest EDIT and its write permission.
 */
export const organizationCapabilities = ({
	plane,
	readOnly,
	permissions,
}: OrganizationCapabilityInput): OrganizationCapabilities => {
	if (plane === TenantConfigPlane.PLATFORM) {
		return { platformNotice: true, forbidden: false, fetchAllowed: false, ...NONE }
	}
	const has = (permission: Permission) => permissions.includes(permission)
	const units = has(Permission.ORGANIZATION_UNIT_READ)
	const types = has(Permission.ORGANIZATION_TYPE_READ)
	const assignments = units && has(Permission.ORGANIZATION_ASSIGNMENT_READ)
	const anyRead = units || types
	const canWrite = (permission: Permission) => !readOnly && has(permission)
	return {
		platformNotice: false,
		forbidden: !anyRead,
		fetchAllowed: anyRead,
		tabs: { units, types },
		panels: { assignments },
		write: {
			units: units && canWrite(Permission.ORGANIZATION_UNIT_WRITE),
			types: types && canWrite(Permission.ORGANIZATION_TYPE_WRITE),
			assignments: assignments && canWrite(Permission.ORGANIZATION_ASSIGNMENT_WRITE),
		},
		userDirectory: has(Permission.USER_READ),
	}
}
